import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormHelperText,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  IconButton,
  Grid,
  Paper
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { createAnnouncement, resetCreateStatus } from '../features/notifications/notificationSlice';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import GroupIcon from '@mui/icons-material/Group';
import EventIcon from '@mui/icons-material/Event';
import CloseIcon from '@mui/icons-material/Close';
import PreviewIcon from '@mui/icons-material/Preview';

// 用戶組別選項
const USER_GROUPS = [
  { value: 'residents', label: '住戶' },
  { value: 'tenants', label: '租戶' },
  { value: 'security', label: '保安人員' },
  { value: 'managers', label: '物業管理員' }
];

// 優先級選項
const PRIORITIES = [
  { value: 'low', label: '低', color: 'success' },
  { value: 'medium', label: '中', color: 'warning' },
  { value: 'high', label: '高', color: 'error' }
];

const AnnouncementCreator = () => {
  const dispatch = useDispatch();
  const { loading, createSuccess, createError } = useSelector(state => state.notifications);
  
  // 表單狀態
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [priority, setPriority] = useState('medium');
  const [targetGroups, setTargetGroups] = useState(['residents', 'tenants']);
  const [expiryDate, setExpiryDate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  // 驗證狀態
  const [errors, setErrors] = useState({
    title: '',
    content: ''
  });

  // 重置表單
  const resetForm = () => {
    setTitle('');
    setContent('');
    setPriority('medium');
    setTargetGroups(['residents', 'tenants']);
    setExpiryDate(null);
    setErrors({
      title: '',
      content: ''
    });
  };

  // 成功創建後清理
  useEffect(() => {
    if (createSuccess) {
      resetForm();
      // 3秒後重置創建狀態
      const timer = setTimeout(() => {
        dispatch(resetCreateStatus());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [createSuccess, dispatch]);
  
  // 表單驗證
  const validateForm = () => {
    const newErrors = {
      title: '',
      content: ''
    };
    
    if (!title.trim()) {
      newErrors.title = '請輸入通告標題';
    } else if (title.length > 100) {
      newErrors.title = '標題不得超過100個字符';
    }
    
    if (!content.trim()) {
      newErrors.content = '請輸入通告內容';
    }
    
    setErrors(newErrors);
    return !newErrors.title && !newErrors.content;
  };
  
  // 處理提交
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const announcementData = {
      title,
      content,
      priority,
      targetGroups,
      type: 'announcement',
      expiryDate
    };
    
    dispatch(createAnnouncement(announcementData));
  };
  
  // 處理目標用戶組變更
  const handleGroupChange = (event) => {
    const {
      target: { value },
    } = event;
    setTargetGroups(
      typeof value === 'string' ? value.split(',') : value
    );
  };
  
  // 切換預覽模式
  const togglePreview = () => {
    setPreviewOpen(!previewOpen);
  };
  
  // 獲取優先級顏色
  const getPriorityColor = (priorityValue) => {
    const priorityObj = PRIORITIES.find(p => p.value === priorityValue);
    return priorityObj ? priorityObj.color : 'default';
  };
  
  return (
    <Box sx={{ mb: 4 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AnnouncementIcon color="primary" sx={{ mr: 1.5, fontSize: 30 }} />
            <Typography variant="h5" component="h2">
              創建通告
            </Typography>
          </Box>
          
          {createSuccess && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon fontSize="inherit" />}
              sx={{ mb: 3 }}
              action={
                <IconButton size="small" onClick={() => dispatch(resetCreateStatus())}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              通告創建成功！
            </Alert>
          )}
          
          {createError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              action={
                <IconButton size="small" onClick={() => dispatch(resetCreateStatus())}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {createError}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="通告標題"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  error={!!errors.title}
                  helperText={errors.title}
                  disabled={loading}
                  placeholder="輸入標題（例如：「小區泳池維修通告」）"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="priority-label">優先級</InputLabel>
                  <Select
                    labelId="priority-label"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    label="優先級"
                    disabled={loading}
                    startAdornment={<PriorityHighIcon sx={{ mr: 1, color: `${getPriorityColor(priority)}.main` }} />}
                  >
                    {PRIORITIES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip
                            size="small"
                            label={option.label}
                            color={option.color}
                            sx={{ mr: 1 }}
                          />
                          {option.value === 'low' && '一般信息'}
                          {option.value === 'medium' && '重要通知'}
                          {option.value === 'high' && '緊急公告'}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="過期日期（可選）"
                    value={expiryDate}
                    onChange={(newValue) => setExpiryDate(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        disabled={loading}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <EventIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        disabled: loading
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="target-groups-label">目標用戶組</InputLabel>
                  <Select
                    labelId="target-groups-label"
                    multiple
                    value={targetGroups}
                    onChange={handleGroupChange}
                    input={<OutlinedInput label="目標用戶組" />}
                    disabled={loading}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const group = USER_GROUPS.find(g => g.value === value);
                          return (
                            <Chip
                              key={value}
                              label={group ? group.label : value}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                    startAdornment={<GroupIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                  >
                    {USER_GROUPS.map((group) => (
                      <MenuItem key={group.value} value={group.value}>
                        {group.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    選擇此通告的目標用戶組
                  </FormHelperText>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="通告內容"
                  multiline
                  rows={6}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  error={!!errors.content}
                  helperText={errors.content}
                  disabled={loading}
                  placeholder="輸入通告詳細內容..."
                />
              </Grid>
              
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={loading}
                    startIcon={<ClearIcon />}
                  >
                    清除
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={togglePreview}
                    disabled={loading}
                    startIcon={<PreviewIcon />}
                  >
                    預覽
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <AnnouncementIcon />}
                  >
                    {loading ? '發佈中...' : '發佈通告'}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
      
      {/* 預覽對話框 */}
      <Dialog
        open={previewOpen}
        onClose={togglePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AnnouncementIcon sx={{ mr: 1.5, color: `${getPriorityColor(priority)}.main` }} />
            通告預覽
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ p: 2 }}>
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {title || '（尚未輸入標題）'}
                </Typography>
                <Chip
                  label={PRIORITIES.find(p => p.value === priority)?.label || '中'}
                  color={getPriorityColor(priority)}
                  size="small"
                />
              </Box>
              
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 3 }}>
                {content || '（尚未輸入內容）'}
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    目標用戶：
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {targetGroups.map((value) => {
                      const group = USER_GROUPS.find(g => g.value === value);
                      return (
                        <Chip
                          key={value}
                          label={group ? group.label : value}
                          size="small"
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>
                </Box>
                
                <Box>
                  {expiryDate && (
                    <Typography variant="caption" color="text.secondary">
                      有效期至：{expiryDate.toLocaleDateString('zh-TW')}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
            
            <DialogContentText variant="body2">
              此為通告預覽。發佈後，目標用戶將立即收到此通告通知。
            </DialogContentText>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={togglePreview}>關閉預覽</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              togglePreview();
              handleSubmit({ preventDefault: () => {} });
            }}
            disabled={loading || !validateForm()}
          >
            確認並發佈
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnnouncementCreator; 